/**
 * FloorPlanOverlay — interactive draggable SKU markers on top of the
 * 2D plan SVG. Same drag pipeline as Scene3D:
 *   - Position resolution: per-room override > category anchor
 *   - Pointer-driven drag with room-interior clamp
 *   - Live agentic tip via spatialAdvisor (Neeraj Bhatia · spatial fit)
 *   - Persist on release through setPlacementPosition
 *   - Timeline event recorded
 *
 * The overlay sits inside the SVG viewBox so coordinates align exactly
 * with the rendered plan. Metres ↔ pixels conversion goes through the
 * PlanMetrics struct returned by floorplanSvg.getPlanMetrics().
 */
import { useEffect, useMemo, useState, useRef } from "react";
import type { GraphNode, ProjectGraph } from "../../types/ontology";
import { type PlanMetrics } from "../../lib/floorplanSvg";
import { adviseDragPosition, type SpatialAdvice } from "../../lib/agent/spatialAdvisor";
import codegen from "../../../../docs/ontology/codegen.json";

function attrNum(n: GraphNode | undefined, key: string): number | null {
  if (!n) return null;
  const v = n.attrs[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function attrStr(n: GraphNode | undefined, key: string): string | null {
  if (!n) return null;
  const v = n.attrs[key];
  return typeof v === "string" ? v : null;
}

/** Single ontology-typed lookup — codegen.json#categoryFootprints rows
 *  carry wM, dM, anchor, color. 2D + 3D + skuFitCheck all read this
 *  one table. Zero inline tables in code. CLAUDE.md §1. */
type Anchor = "center" | "south" | "north" | "east" | "west" | "overhead";
interface CategoryRow {
  category: string;
  wM: number;
  dM: number;
  hM?: number;
  anchor: Anchor;
  color: string;
}
const CATEGORY_TABLE = new Map<string, CategoryRow>(
  (codegen.categoryFootprints as ReadonlyArray<CategoryRow>).map((r) => [r.category, r]),
);
function categoryRow(cat: string): CategoryRow {
  return CATEGORY_TABLE.get(cat) ?? CATEGORY_TABLE.get("Item")!;
}

function categoryOf(sku: GraphNode): string {
  return attrStr(sku, "category") ?? "Item";
}

function defaultPositionMetres(
  cat: string, roomW: number, roomD: number, slot: number,
): { xRel: number; zRel: number } {
  const r = categoryRow(cat);
  const halfW = r.wM / 2, halfD = r.dM / 2;
  const inset = 0.4;
  const cx = roomW / 2, cz = roomD / 2;
  switch (r.anchor) {
    case "south":    return { xRel: cx + (slot - 1) * 0.2, zRel: roomD - halfD - inset };
    case "north":    return { xRel: cx + (slot - 1) * 0.2, zRel: halfD + inset };
    case "east":     return { xRel: roomW - halfW - inset, zRel: cz + (slot - 1) * 0.3 };
    case "west":     return { xRel: halfW + inset, zRel: cz + (slot - 1) * 0.3 };
    case "overhead": return { xRel: cx + (slot - 1) * 0.5, zRel: cz };
    case "center":
    default:         return { xRel: cx + (slot - 1) * 0.5, zRel: cz };
  }
}

interface PlacedMarker {
  readonly sku: GraphNode;
  /** Metres relative to room origin. */
  readonly xRelM: number;
  readonly zRelM: number;
  /** AABB half-extents AFTER rotation. */
  readonly halfWM: number;
  readonly halfDM: number;
  /** Unrotated half-extents (used to render the rect's own size; the
   *  full rect is rotated visually so it follows the yaw). */
  readonly halfWMUnrot: number;
  readonly halfDMUnrot: number;
  /** Yaw in radians (snapped to 90° increments via setPlacementPosition). */
  readonly yaw: number;
  /** Category anchor — "overhead" is skipped from collision detection
   *  because lighting and ceiling fans never physically collide with
   *  floor furniture even though their footprint overlaps in plan. */
  readonly anchor: Anchor;
  /** True when this marker's rotated AABB overlaps a non-overhead
   *  sibling by > COLLISION_TOL_M on both axes. Same source-of-truth
   *  as Scene3D's PlacedSku.colliding and agentSuggestions
   *  R-spatial-collision verdict. */
  readonly colliding: boolean;
}

const COLLISION_TOL_M = 0.01;

function rotatedAABB(halfW: number, halfD: number, yaw: number): { halfWRot: number; halfDRot: number } {
  const c = Math.abs(Math.cos(yaw));
  const s = Math.abs(Math.sin(yaw));
  return { halfWRot: c * halfW + s * halfD, halfDRot: s * halfW + c * halfD };
}

function resolvePlacedMarkers(graph: ProjectGraph, roomId: string, roomW: number, roomD: number): ReadonlyArray<PlacedMarker> {
  // SKUs placed in this room via IN_ROOM edges.
  const ids = new Set(graph.edges.filter((e) => e.kind === "IN_ROOM" && e.target === roomId).map((e) => e.source));
  const skus = graph.nodes.filter((n) => n.kind === "SKU" && ids.has(n.id));
  const counters: Record<string, number> = {};
  type Draft = Omit<PlacedMarker, "colliding">;
  const draft: Draft[] = skus.map<Draft>((sku) => {
    const cat = categoryOf(sku);
    const r = categoryRow(cat);
    const overrideX = attrNum(sku, `posX_${roomId}`);
    const overrideZ = attrNum(sku, `posZ_${roomId}`);
    const yawAttr = attrNum(sku, `posYaw_${roomId}`);
    const yaw = yawAttr ?? 0;
    const halfWUnrot = r.wM / 2, halfDUnrot = r.dM / 2;
    const { halfWRot, halfDRot } = rotatedAABB(halfWUnrot, halfDUnrot, yaw);
    if (overrideX !== null && overrideZ !== null) {
      const xRel = Math.max(halfWRot, Math.min(roomW - halfWRot, overrideX));
      const zRel = Math.max(halfDRot, Math.min(roomD - halfDRot, overrideZ));
      return { sku, xRelM: xRel, zRelM: zRel, halfWM: halfWRot, halfDM: halfDRot, halfWMUnrot: halfWUnrot, halfDMUnrot: halfDUnrot, yaw, anchor: r.anchor };
    }
    counters[cat] = (counters[cat] ?? 0) + 1;
    const pos = defaultPositionMetres(cat, roomW, roomD, counters[cat]!);
    return { sku, xRelM: pos.xRel, zRelM: pos.zRel, halfWM: halfWRot, halfDM: halfDRot, halfWMUnrot: halfWUnrot, halfDMUnrot: halfDUnrot, yaw, anchor: r.anchor };
  });

  // Pairwise AABB overlap on rotated half-extents — same logic as
  // Scene3D.placeSkusInRoom and agentSuggestions.detectSpacingDensity.
  const colliding = new Set<string>();
  for (let i = 0; i < draft.length; i++) {
    const a = draft[i]!;
    if (a.anchor === "overhead") continue;
    for (let j = i + 1; j < draft.length; j++) {
      const b = draft[j]!;
      if (b.anchor === "overhead") continue;
      const overlapX = a.halfWM + b.halfWM - Math.abs(a.xRelM - b.xRelM);
      const overlapZ = a.halfDM + b.halfDM - Math.abs(a.zRelM - b.zRelM);
      if (overlapX > COLLISION_TOL_M && overlapZ > COLLISION_TOL_M) {
        colliding.add(a.sku.id);
        colliding.add(b.sku.id);
      }
    }
  }

  return draft.map<PlacedMarker>((d) => ({ ...d, colliding: colliding.has(d.sku.id) }));
}

interface Props {
  readonly graph: ProjectGraph;
  readonly roomId: string;
  readonly metrics: PlanMetrics;
  readonly onSkuMoved: (skuId: string, roomId: string, xRelM: number, zRelM: number) => void;
  /** Rotate the selected SKU about its Y axis. `nextYaw` is the absolute
   *  target yaw (snapped to 90° increments by the caller). */
  readonly onSkuRotated?: (skuId: string, roomId: string, nextYaw: number) => void;
}

export function FloorPlanOverlay({ graph, roomId, metrics, onSkuMoved, onSkuRotated }: Props): JSX.Element | null {
  const roomWM = metrics.roomFt.w / metrics.ftPerM;
  const roomDM = metrics.roomFt.d / metrics.ftPerM;
  const markers = useMemo(
    () => resolvePlacedMarkers(graph, roomId, roomWM, roomDM),
    [graph, roomId, roomWM, roomDM],
  );

  const [dragging, setDragging] = useState<{ skuId: string; xRelM: number; zRelM: number } | null>(null);
  const [advice, setAdvice] = useState<ReadonlyArray<SpatialAdvice>>([]);
  const [selectedSkuId, setSelectedSkuId] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  // R-key rotates the selected SKU 90° CCW; Shift+R for CW. Same snap
  // rules as Scene3D so 2D + 3D rotations stay in sync.
  useEffect(() => {
    if (!onSkuRotated) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key.toLowerCase() !== "r") return;
      const target = dragging?.skuId ?? selectedSkuId;
      if (!target) return;
      const m = markers.find((x) => x.sku.id === target);
      if (!m) return;
      const delta = e.shiftKey ? -Math.PI / 2 : Math.PI / 2;
      const nextYaw = ((m.yaw + delta + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
      const snapped = Math.round(nextYaw / (Math.PI / 2)) * (Math.PI / 2);
      onSkuRotated(target, roomId, snapped);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dragging, selectedSkuId, markers, onSkuRotated, roomId]);

  // Metres → SVG-units conversion. The viewBox is in feet*pxPerFt; we
  // need to walk through feet to keep parity with the SVG renderer.
  const mToPx = (m: number): number => m * metrics.ftPerM * metrics.pxPerFt;

  function svgPointFromEvent(e: React.PointerEvent<SVGRectElement | SVGSVGElement>): { xPx: number; yPx: number } | null {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const local = pt.matrixTransform(ctm.inverse());
    return { xPx: local.x, yPx: local.y };
  }

  /** Convert an SVG-point inside the room rectangle to room-relative
   *  metres. Clamps to room interior — the spatial advisor still
   *  highlights "0.42 m to wall" warnings, but the SKU never leaves
   *  the room. */
  function svgToRoomMetres(xPx: number, yPx: number, halfWM: number, halfDM: number): { xRelM: number; zRelM: number } {
    const xRelPx = xPx - metrics.originPx.x;
    const zRelPx = yPx - metrics.originPx.y;
    const xRelM = xRelPx / metrics.pxPerFt / metrics.ftPerM;
    const zRelM = zRelPx / metrics.pxPerFt / metrics.ftPerM;
    return {
      xRelM: Math.max(halfWM, Math.min(roomWM - halfWM, xRelM)),
      zRelM: Math.max(halfDM, Math.min(roomDM - halfDM, zRelM)),
    };
  }

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${metrics.viewBox.w} ${metrics.viewBox.h}`}
      preserveAspectRatio="xMidYMid meet"
      className="absolute inset-0 w-full h-full"
      style={{ touchAction: "none" }}
    >
      {markers.map((m) => {
        const isDragging = dragging?.skuId === m.sku.id;
        const isSelected = selectedSkuId === m.sku.id;
        const xRelM = isDragging ? dragging.xRelM : m.xRelM;
        const zRelM = isDragging ? dragging.zRelM : m.zRelM;
        // Rendered rect uses the UNROTATED dims + an SVG rotation, so
        // the visual matches the 3D yaw exactly. AABB clamp still uses
        // the rotated halves (set in resolvePlacedMarkers).
        const cxPx = metrics.originPx.x + mToPx(xRelM);
        const cyPx = metrics.originPx.y + mToPx(zRelM);
        const wUnrot = mToPx(m.halfWMUnrot * 2);
        const hUnrot = mToPx(m.halfDMUnrot * 2);
        // SVG rotation uses degrees, clockwise. Three.js yaw about +Y
        // is counter-clockwise looking down — invert sign to match.
        const yawDeg = -(m.yaw * 180) / Math.PI;
        const tone = advice[0]?.tone ?? "ok";
        const cat = categoryOf(m.sku);
        const baseColor = categoryRow(cat).color;
        // Collision wins over every other stroke colour — designers see
        // exactly which SKU overlaps without opening the audit drawer.
        // Same red as Scene3D's SCENE_COLORS.collision (#D14747).
        const stroke = m.colliding
          ? "#D14747"
          : isDragging
            ? (tone === "block" ? "#C95C1A" : tone === "warn" ? "#A04612" : "#3F7A55")
            : isSelected ? "#C95C1A" : "#141612";
        const strokeWidth = m.colliding ? 3.4 : (isDragging || isSelected ? 3 : 1.6);
        const labelShort = m.sku.label.split(" · ")[0] ?? m.sku.label;
        // Persistent wall-distance dimensions when selected — addresses
        // "I can't measure distance from wall". Computed against the
        // ROTATED AABB so the numbers stay honest after rotation.
        const distN = xRelM - m.halfWM > 0 ? xRelM - m.halfWM : 0;  // west wall actually
        const distW = zRelM - m.halfDM > 0 ? zRelM - m.halfDM : 0;
        const distE = roomWM - (xRelM + m.halfWM);
        const distS = roomDM - (zRelM + m.halfDM);
        return (
          <g key={m.sku.id}>
            {/* Persistent wall-distance dimensions for the SELECTED SKU.
                Four leader lines + labels in metres. */}
            {(isSelected || isDragging) ? (
              <g pointerEvents="none" style={{ fontFamily: "-apple-system, 'SF Pro Text', Inter, sans-serif" }}>
                {/* West (left) wall distance */}
                <line x1={metrics.originPx.x} y1={cyPx} x2={cxPx - mToPx(m.halfWM)} y2={cyPx} stroke="#7a766f" strokeWidth={1} strokeDasharray="4 3" opacity={0.7} />
                <text x={metrics.originPx.x + mToPx(distN) / 2} y={cyPx - 4} textAnchor="middle" fill="#7a766f" fontSize={10} style={{ fontVariantNumeric: "tabular-nums" }}>{distN.toFixed(2)} m</text>
                {/* East (right) wall distance */}
                <line x1={cxPx + mToPx(m.halfWM)} y1={cyPx} x2={metrics.originPx.x + mToPx(roomWM)} y2={cyPx} stroke="#7a766f" strokeWidth={1} strokeDasharray="4 3" opacity={0.7} />
                <text x={cxPx + mToPx(m.halfWM) + mToPx(distE) / 2} y={cyPx - 4} textAnchor="middle" fill="#7a766f" fontSize={10} style={{ fontVariantNumeric: "tabular-nums" }}>{distE.toFixed(2)} m</text>
                {/* North (top) wall distance */}
                <line x1={cxPx} y1={metrics.originPx.y} x2={cxPx} y2={cyPx - mToPx(m.halfDM)} stroke="#7a766f" strokeWidth={1} strokeDasharray="4 3" opacity={0.7} />
                <text x={cxPx + 6} y={metrics.originPx.y + mToPx(distW) / 2} textAnchor="start" fill="#7a766f" fontSize={10} style={{ fontVariantNumeric: "tabular-nums" }}>{distW.toFixed(2)} m</text>
                {/* South (bottom) wall distance */}
                <line x1={cxPx} y1={cyPx + mToPx(m.halfDM)} x2={cxPx} y2={metrics.originPx.y + mToPx(roomDM)} stroke="#7a766f" strokeWidth={1} strokeDasharray="4 3" opacity={0.7} />
                <text x={cxPx + 6} y={cyPx + mToPx(m.halfDM) + mToPx(distS) / 2} textAnchor="start" fill="#7a766f" fontSize={10} style={{ fontVariantNumeric: "tabular-nums" }}>{distS.toFixed(2)} m</text>
              </g>
            ) : null}
            <rect
              x={cxPx - wUnrot / 2}
              y={cyPx - hUnrot / 2}
              width={wUnrot}
              height={hUnrot}
              transform={`rotate(${yawDeg} ${cxPx} ${cyPx})`}
              fill={baseColor}
              fillOpacity={m.colliding ? 0.55 : isDragging ? 0.6 : isSelected ? 0.45 : 0.32}
              stroke={stroke}
              strokeWidth={strokeWidth}
              strokeDasharray={m.colliding ? "6 3" : undefined}
              rx={3}
              style={{ pointerEvents: "auto", cursor: isDragging ? "grabbing" : "grab", touchAction: "none" }}
              onClickCapture={(e) => {
                // Selection on a non-drag click so the rotate handle appears.
                if (!isDragging) {
                  e.stopPropagation();
                  setSelectedSkuId(m.sku.id);
                }
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
                (e.target as Element).setPointerCapture?.(e.pointerId);
                const pt = svgPointFromEvent(e);
                if (!pt) return;
                const { xRelM: xr, zRelM: zr } = svgToRoomMetres(pt.xPx, pt.yPx, m.halfWM, m.halfDM);
                setDragging({ skuId: m.sku.id, xRelM: xr, zRelM: zr });
              }}
              onPointerMove={(e) => {
                if (!dragging || dragging.skuId !== m.sku.id) return;
                const pt = svgPointFromEvent(e);
                if (!pt) return;
                const { xRelM: xr, zRelM: zr } = svgToRoomMetres(pt.xPx, pt.yPx, m.halfWM, m.halfDM);
                setDragging({ skuId: m.sku.id, xRelM: xr, zRelM: zr });
                // Live agentic clearance verdict — same code path as 3D.
                const siblings = markers.filter((other) => other.sku.id !== m.sku.id).map((other) => ({
                  id: other.sku.id,
                  label: other.sku.label.split(" · ")[0] ?? other.sku.label,
                  xRel: other.xRelM,
                  zRel: other.zRelM,
                  halfW: other.halfWM,
                  halfD: other.halfDM,
                }));
                setAdvice(adviseDragPosition({
                  category: categoryOf(m.sku),
                  halfW: m.halfWM,
                  halfD: m.halfDM,
                  xRel: xr,
                  zRel: zr,
                  roomW: roomWM,
                  roomD: roomDM,
                  siblings,
                }));
              }}
              onPointerUp={(e) => {
                if (!dragging || dragging.skuId !== m.sku.id) return;
                (e.target as Element).releasePointerCapture?.(e.pointerId);
                onSkuMoved(m.sku.id, roomId, dragging.xRelM, dragging.zRelM);
                setDragging(null);
                setAdvice([]);
              }}
            >
              <title>{m.sku.label}</title>
            </rect>
            {/* Label rotates with the rect — group transform keeps the
                text upright relative to the SKU's orientation. */}
            {wUnrot > 60 && hUnrot > 18 ? (
              <text
                x={cxPx}
                y={cyPx + 4}
                transform={`rotate(${yawDeg} ${cxPx} ${cyPx})`}
                textAnchor="middle"
                fill="#141612"
                fontSize={Math.min(13, Math.max(9, hUnrot / 4))}
                fontFamily="-apple-system, 'SF Pro Text', Inter, sans-serif"
                fontWeight={500}
                style={{ pointerEvents: "none" }}
              >
                {labelShort.length > 16 ? labelShort.slice(0, 14) + "…" : labelShort}
              </text>
            ) : null}
            {/* Rotate handle — appears for the selected SKU. SVG-native
                button (foreignObject would break in some renderers). */}
            {isSelected && onSkuRotated ? (() => {
              const btnX = cxPx + mToPx(m.halfWM) + 8;
              const btnY = cyPx - 12;
              return (
                <g>
                  <rect
                    x={btnX} y={btnY} width={28} height={24} rx={5}
                    fill="#C95C1A"
                    style={{ pointerEvents: "auto", cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const delta = e.shiftKey ? -Math.PI / 2 : Math.PI / 2;
                      const nextYaw = ((m.yaw + delta + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
                      onSkuRotated(m.sku.id, roomId, Math.round(nextYaw / (Math.PI / 2)) * (Math.PI / 2));
                    }}
                  >
                    <title>Rotate 90° (R) · Shift-click for -90°</title>
                  </rect>
                  <text x={btnX + 14} y={btnY + 16} textAnchor="middle" fill="#FBF5E9" fontSize={14} fontWeight={600} style={{ pointerEvents: "none" }}>↻</text>
                </g>
              );
            })() : null}
            {/* Drag-tip overlay — persona-attributed, citation-pinned. */}
            {isDragging && advice.length > 0 ? (() => {
              const worst = advice[0]!;
              const bg = worst.tone === "block" ? "#C95C1A" : worst.tone === "warn" ? "#A04612" : "#3F7A55";
              const tipX = cxPx;
              const tipY = Math.max(40, cyPx - mToPx(m.halfDM) - 8);
              return (
                <g pointerEvents="none">
                  <rect x={tipX - 110} y={tipY - 36} width={220} height={36} rx={6} fill={bg} opacity={0.96} />
                  <text x={tipX} y={tipY - 22} textAnchor="middle" fill="#FBF5E9" fontSize={9} fontFamily="-apple-system, 'SF Pro Text', Inter, sans-serif" letterSpacing={0.4}>
                    NEERAJ BHATIA · SPATIAL FIT
                  </text>
                  <text x={tipX} y={tipY - 10} textAnchor="middle" fill="#FBF5E9" fontSize={11} fontFamily="-apple-system, 'SF Pro Text', Inter, sans-serif" fontWeight={600}>
                    {worst.title.length > 36 ? worst.title.slice(0, 34) + "…" : worst.title}
                  </text>
                </g>
              );
            })() : null}
          </g>
        );
      })}
    </svg>
  );
}
